import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { CentEuroFieldMiddleware } from '@adornis/formfield/field-middlewares/CentEuroFieldMiddleware.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { IBANInput } from '@adornis/formfield/fields/iban-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { usePopover } from '@adornis/functional-lit/use-popover.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { and } from '@adornis/validation/functions/and.js';
import { iban } from '@adornis/validation/functions/iban.js';
import { CardOptionsMenu, type CardOptionsMenuItem } from '@martinus/styles/client/menus/CardOptionsMenu.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  BillingPositionPriceSymbols,
  BillingRhythm,
  isDynamicPriceInCents,
} from '../../billingPositions/entities/BillingPosition.js';
import { Booking } from '../../bookings/entities/Booking.js';
import { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { MartinusTemporaryActions } from '../../MartinusTemporaryPolicy.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import { Permission, Role } from '../../permissions/roles.js';
import { getSchoolYear } from '../../schoolyear/getSchoolYear.js';
import { Person } from '../../users/entities/person.js';
import { hasFixedAmount, immediateEndDay, isStartMovableByOwner } from '../api/mandateActivity.js';
import { MandatePhase } from '../api/MandatStatus.js';
import { SepaAPI } from '../api/SepaAPI.js';
import {
  mandateEndNotBackdated,
  mandateRuntimeNotNegative,
  mandateStartNotBackdated,
} from '../api/validateMandateEnd.js';
import { SepaMandat } from '../entities/SepaMandat.js';
import { type SepaMandateAbacPayload, SepaMandateActions } from '../SepaMandatePolicy.js';
import { getMandateLifecycle } from './mandateLifecycle.js';
import { openSepaChangeUserConfirmDialog, SepaUserChangeDecision } from './SepaChangeUserConfirmDialog.js';

export const SepaCardSkeleton = () => html`
  <div class="card w-full max-w-[500px]">
    <div class="card-body gap-4">
      <!-- Header with title and status -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="skeleton h-6 w-32"></div>
          <div class="skeleton h-6 w-16 rounded-full"></div>
        </div>
      </div>

      <!-- Metadata badges -->
      <div class="flex flex-wrap items-center gap-2">
        <div class="skeleton h-4 w-24"></div>
        <div class="skeleton h-4 w-32"></div>
      </div>

      <!-- Amount section -->
      <div class="flex flex-col gap-2 p-4 bg-base-200/50 rounded-lg">
        <div class="skeleton h-4 w-16"></div>
        <div class="skeleton h-8 w-28"></div>
      </div>

      <!-- IBAN -->
      <div class="flex flex-col gap-2">
        <div class="skeleton h-4 w-12"></div>
        <div class="skeleton h-12 w-full"></div>
      </div>

      <!-- Date range -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <div class="skeleton h-4 w-12"></div>
          <div class="skeleton h-12 w-full"></div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="skeleton h-4 w-12"></div>
          <div class="skeleton h-12 w-full"></div>
        </div>
      </div>

      <!-- Action button -->
      <div class="card-actions pt-2">
        <div class="skeleton h-12 w-full"></div>
      </div>
    </div>
  </div>
`;

const getSpecialFormatCases = (cents: number) => {
  const normalize = (val: string) => {
    return parseInt(val, 10) * 100;
  };

  // the dynamic placeholder is no amount yet - it is filled in per usage
  if (isDynamicPriceInCents(cents)) return 'Noch nicht festgelegt';
  if (cents === normalize(BillingPositionPriceSymbols.PAUSED)) return 'Pausiert';
  if (cents === normalize(BillingPositionPriceSymbols.EXCEMPT)) return 'Befreit';
  if (cents === normalize(BillingPositionPriceSymbols.FUNDED)) return 'Gefördert';
  return null;
};

const formatMoney = (cents?: Maybe<number>) => {
  if (cents == null) return '0.00€';
  const parsed = getSpecialFormatCases(cents);
  if (parsed) return parsed;

  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)}€`;
  }
};

export const SepaMandateCard = stateful((props: { mandat: SepaMandat; userID: Maybe<string> }) => {
  const { value: referencedBooking, loading: referencedBookingLoading } = useQuery(
    () =>
      subscribeByID(
        Booking,
        props.mandat.bookingID,
      )({
        groupIDs: 1,
        grade: 1,
        startDate: 1,
        classIdentifier: 1,
        userID: 1,
        groups: { _id: 1, name: 1 },
      }),
    [props.mandat.bookingID],
  );

  const { value: oldReferencedBookings, loading: oldReferencedBookingLoading } = useQuery(
    () =>
      subscribeByID(
        ClassBooking,
        props.mandat.pastBookingIDs,
      )({
        groupIDs: 1,
        grade: 1,
        startDate: 1,
        endDate: 1,
        classIdentifier: 1,
        userID: 1,
        groups: { _id: 1, name: 1 },
      }),
    [props.mandat.pastBookingIDs],
  );

  const bookingGroup = referencedBooking?.groups?.find(group => group._id === props.mandat.groupID);
  const schoolYear =
    referencedBooking?._class === ClassBooking._class && referencedBooking.startDate
      ? getSchoolYear({ date: referencedBooking.startDate })
      : null;

  const { value: referencedUser, loading: referencedUserLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        referencedBooking?.userID,
      )({ _id: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
    [referencedBooking?.userID],
  );

  const { value: person, loading: userLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.userID,
      )({
        _id: 1,
        roles: { name: 1, contexts: 1 },
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
        debitorennummer: 1,
      }),
    [props.userID],
  );

  const userIsStudent = person ? hasRoles({ user: person, roles: [Role.SCHUELER] }) : true;

  const { can: canManageOthers } = useMartinusABAC({ action: SepaMandateActions.SEPA_MANDATE_MANAGE_OTHERS });
  // the old bookings open on click and stay open for scrolling, so a popover instead of a hover tooltip
  const history = usePopover({ positionArea: 'bottom', closeOnClickOutside: true });

  /**
   * `billedOn` is the Stichtag of our own export, not the day the bank draws the money, so it is
   * only shown to those who edit other people's bookings (Buchhaltung, Admin) - never to the
   * Sorgeberechtigte themselves.
   */
  const canSeeBilledOn = !!useObservable(() => currentUser<Person>(), [])?.hasPermission(Permission.EDIT_BOOKINGS);

  /**
   * `useForm`'s document is a Proxy over `props.mandat` *itself*, so a picked value is written
   * straight onto the mandate - after an edit `props.mandat.end` reads back the edit, not the
   * stored value. Snapshotted before the form can touch it, because this is what "the end did not
   * change" has to be measured against. The same goes for the runtime as a whole.
   */
  const [storedEnd] = useState(props.mandat.end, [props.mandat]);
  const [storedStart] = useState(props.mandat.start, [props.mandat]);
  const runtimeNotNegative = mandateRuntimeNotNegative({ previous: { start: storedStart, end: storedEnd } });
  // judged on the stored mandate: accepting with a picked start must not lock the field mid-edit
  const [startMovable] = useState(canManageOthers || isStartMovableByOwner(props.mandat), [
    props.mandat,
    canManageOthers,
  ]);

  const { document, validate, field } = useForm(
    props.mandat,
    {
      validator: {
        rhythm: sgNonOptional(),
        iban: and(iban(), sgNonOptional()),
        amountInCents: sgNonOptional(),
        // the runtime is judged as a whole, so moving either bound past the other shows on that field
        start: and(
          mandateStartNotBackdated({ canBackdate: canManageOthers, previousStart: storedStart }),
          runtimeNotNegative,
        ),
        end: and(mandateEndNotBackdated({ canBackdate: canManageOthers, previousEnd: storedEnd }), runtimeNotNegative),
      },
    },
    [props.mandat, canManageOthers],
  );

  // judged on the stored mandate: the form writes its edits onto `props.mandat` itself
  const [lifecycle] = useState(getMandateLifecycle(props.mandat, canManageOthers), [props.mandat, canManageOthers]);

  /**
   * Mandates from before the flag existed carry the dynamic placeholder only in their amount, so
   * the amount decides as well.
   */
  const isDynamic = !!props.mandat.isDynamic || isDynamicPriceInCents(props.mandat.amountInCents);

  const { can: canEdit } = useMartinusABAC<SepaMandateAbacPayload>(
    {
      action: SepaMandateActions.SEPA_MANDATE_EDIT,
      mandat: { accepted: props.mandat.accepted, acceptedByID: props.mandat.acceptedByID },
      viewedUser: person,
    },
    [props.mandat.accepted, props.mandat.acceptedByID],
  );
  // nothing typed could be saved - a locked group, a funded or billed mandate, or no edit right
  const inputsDisabled = !canEdit || lifecycle.readonly || !(lifecycle.canSave || lifecycle.canAccept);
  const { can: canChangeAcceptor } = useMartinusABAC<SepaMandateAbacPayload>({
    action: SepaMandateActions.SEPA_MANDATE_CHANGE_ACCEPTOR,
    viewedUser: person,
  });
  const { can: canDelete } = useMartinusABAC<SepaMandateAbacPayload>({
    action: SepaMandateActions.SEPA_MANDATE_DELETE,
    viewedUser: person,
  });
  const { can: canReframe } = useMartinusABAC<SepaMandateAbacPayload>({
    action: SepaMandateActions.SEPA_MANDATE_REFRAME,
    viewedUser: person,
  });
  const { can: canViewGroups } = useMartinusABAC<SepaMandateAbacPayload>({
    action: MartinusTemporaryActions.VIEW_GROUPS,
    viewedUser: person,
  });

  const saveDocument = (successText: string) =>
    guardDialog(() => SepaAPI.save(document)(), { success: () => SgSnackbar.show(successText) });

  const earliestStart = todayAsCalendarDay();
  const laterStart = (picked: Maybe<Date>) => (picked && picked > earliestStart ? picked : earliestStart);

  const onAccept = async () => {
    const errors = await validate();
    if (errors) return;

    /**
     * The mandate runs from the picked day (e.g. "from next month"), or from the day of the
     * consent - whatever the booking's runtime says. Until then the export bills the booking as
     * Selbstzahler.
     */
    const consent = {
      start: laterStart(document.start),
      acceptedByID: props.userID,
      acceptedAt: new Date(),
      accepted: true,
    };

    /**
     * A template with a Mandatsreferenz is an ended mandate of the booking (`findReusableMandate`). The
     * person decides whether it is reactivated - same row, same Mandatsreferenz - or a new mandate is given.
     */
    let reactivate = false;
    if (document.mandateReference) {
      // not `confirmDialog`: it reads Escape as the decline, which here would give a new mandate
      try {
        reactivate = await showDialog<boolean>({
          modal: true,
          closeOnEscape: true,
          content: (choose, cancel) => html`
            <div class="engine-dialog-content">
              <p>
                Es gibt bereits ein beendetes Mandat mit der Referenz ${document.mandateReference}. Soll es reaktiviert
                werden, oder soll ein neues Mandat erstellt werden?
              </p>
              <div class="engine-dialog-actions flex flex-row flex-wrap justify-end gap-3">
                <button type="button" class="btn btn-ghost mr-auto" @click=${() => cancel()}>Abbrechen</button>
                <button type="button" class="btn btn-outline" @click=${() => choose(false)}>
                  Neues Mandat erteilen
                </button>
                <button type="button" class="btn btn-primary" @click=${() => choose(true)}>Mandat reaktivieren</button>
              </div>
            </div>
          `,
        });
      } catch {
        // cancelled (Abbrechen or Escape): nothing is accepted
        return;
      }
    }

    if (document.mandateReference && !reactivate) {
      // built as its own entity, so the template in the list keeps its key until the query refreshes
      const fresh = new SepaMandat({
        ...document.toJSON(),
        ...consent,
        _id: undefined,
        mandateReference: undefined,
        billedOn: undefined,
        pastBookingIDs: undefined,
        oldBooking: undefined,
      });
      await guardDialog(() => SepaAPI.save(fresh)(), { success: () => SgSnackbar.show('SEPA Mandat aktiviert') });
      return;
    }

    Object.assign(document, consent);
    await saveDocument(reactivate ? 'SEPA Mandat reaktiviert' : 'SEPA Mandat aktiviert');
  };

  const onEnd = async () => {
    if (
      !(await confirmDialog('Möchten Sie das SEPA-Mandat wirklich beenden?', {
        highlightDecline: true,
        declineText: 'Abbrechen',
        acceptText: 'SEPA-Mandat beenden',
      }))
    )
      return;
    /**
     * The mandate ran for the last time yesterday, so it is no longer collected by an export with
     * today's Stichtag. One that has not started yet ends the day before it would have.
     */
    document.end = immediateEndDay(document.start);
    await saveDocument('SEPA Mandat beendet');
  };

  const onSave = async () => {
    document.acceptedByID ??= props.userID;

    const errors = await validate();
    if (errors) return;

    await saveDocument('SEPA Mandat gespeichert');
  };

  /**
   * Advanced administrative action: move the mandate to the currently shown person.
   */
  const onChangeAcceptor = async () => {
    try {
      const result = await openSepaChangeUserConfirmDialog(person);
      if (result !== SepaUserChangeDecision.CHANGE) return;
    } catch {
      return;
    }

    document.acceptedByID = props.userID;
    document.acceptedAt = new Date();

    await saveDocument('Akzeptierende Person geändert');
  };

  const onDelete = async () => {
    if (
      !(await confirmDialog('Mandat wirklich löschen?', {
        acceptText: 'Löschen',
        declineText: 'Abbrechen',
        highlightDecline: true,
      }))
    )
      return;
    await guardDialog(
      async () => {
        await document.remove();
      },
      { successText: 'Mandat gelöscht' },
    );
  };

  const onReframe = async () => {
    if (
      !(await confirmDialog(
        'Möchten Sie die Mandatswerte mit der Gruppe abgleichen? Dies setzt die Werte (Preis, Buchungsdaten und Rhythmus) auf die Werte der Buchung/Gruppe.',
        {
          highlightDecline: true,
          declineText: 'Abbrechen',
          acceptText: 'Mandatswerte abgleichen',
        },
      ))
    )
      return;

    if (!document._id) {
      return;
    }

    await guardDialog(
      async () => {
        await SepaAPI.reframe(document._id)();
      },
      { successText: 'Mandatswerte wurden mit der Buchung/Gruppe abgeglichen.' },
    );
  };

  const statusBadge = () =>
    lifecycle.label ? html`<div class="badge ${lifecycle.badgeClass}">${lifecycle.label}</div>` : nothing;

  /**
   * Advanced actions (delete, switching the accepting person) are only available
   * for administrative personnel and live in a per-mandate options menu.
   */
  const optionsMenu = () => {
    if (!document._id) return nothing;

    const items: CardOptionsMenuItem[] = [];

    if (canChangeAcceptor && document.accepted && document.acceptedByID !== props.userID) {
      items.push({
        label: 'Akzeptierende Person wechseln',
        iconifyClass: 'icon-[material-symbols--swap-horiz]',
        onClick: onChangeAcceptor,
      });
    }

    if (canReframe) {
      items.push({
        label: 'Mandatswerte mit Gruppe abgleichen',
        iconifyClass: 'icon-[material-symbols--refresh]',
        onClick: onReframe,
      });
    }
    if (canDelete) {
      items.push({
        label: 'Mandat löschen',
        iconifyClass: 'icon-[material-symbols--delete-outline]',
        intent: 'danger',
        onClick: onDelete,
      });
    }

    return CardOptionsMenu({ items, ariaLabel: 'Mandat-Optionen' });
  };

  /**
   * Primary actions are driven by the mandate lifecycle:
   * - pending acceptance -> accept
   * - active / planned   -> save changes, end mandate
   * - ended              -> read only; reactivated by accepting the booking's template again
   * - funded             -> read only, no actions
   */
  const actionButtons = () => {
    if (lifecycle.readonly || !canEdit) return nothing;

    return html`
      ${
        lifecycle.canEnd && document._id
          ? html`<button class="btn btn-error btn-outline btn-lg" @click=${onEnd}>Mandat beenden</button>`
          : nothing
      }
      ${
        lifecycle.canSave
          ? html`<button class="btn btn-primary btn-outline btn-lg" @click=${onSave}>Speichern</button>`
          : nothing
      }
      ${
        lifecycle.phase === MandatePhase.TEMPLATE && props.mandat.mandateReference
          ? html`<button class="btn btn-success btn-lg" @click=${onAccept}>Mandat reaktivieren</button>`
          : lifecycle.canAccept
            ? html`<button class="btn btn-success btn-lg" @click=${onAccept}>Mandat zustimmen</button>`
            : nothing
      }
    `;
  };

  const showSensitiveInputs = (value: Maybe<number> | Maybe<string>) => {
    if (!document._id) return true;
    if (isNullish(value)) return true;

    /**
     * Funded mandates need no inputs
     */
    if (lifecycle.funded) return true;

    if (canManageOthers) return true;

    const isSelf = person?._id === document.acceptedByID;
    return isSelf;
  };

  const trans: Record<BillingRhythm, string> = {
    [BillingRhythm.MONTHLY]: 'Monatlich',
    [BillingRhythm.YEARLY]: 'Jährlich',
    [BillingRhythm.ONCE_CUSTOM]: 'Einmalig',
  };

  const renderOldBookingInfo = () => {
    if (!oldReferencedBookings?.length) return nothing;

    return html`
      <button
        type="button"
        class="btn btn-ghost btn-sm btn-circle"
        aria-label="Historie"
        style="anchor-name: ${history.anchorName};"
        ${ref(history.anchorRef)}
        @click=${() => {
          if (history.visible) history.hide();
          else history.show();
        }}
      >
        <span class="icon-[material-symbols--history-rounded] size-5"></span>
      </button>
      <div
        popover="manual"
        tabindex="-1"
        id=${history.popoverId}
        style="position-anchor: ${history.anchorName}; ${history.getAnchorStyles('position-try-fallbacks: flip-block;')}"
        class="bg-base-100 text-base-content shadow-2xl border border-base-300 rounded-box p-2 max-w-lg max-h-96 overflow-y-auto"
        ${ref(history.popoverRef)}
      >
        ${repeat(
          oldReferencedBookings,
          booking => html`
            <div class="card flex">
              <div class="card-title">Historie</div>
              <div class="flex gap-4">
                <span class="text-base-content/70">•</span>
                <div class="flex gap-1">
                  ${booking.startDate ? formatCalendarDay(booking.startDate, 'dd.MM.yyyy') : ''} -
                  ${booking.endDate ? formatCalendarDay(booking.endDate, 'dd.MM.yyyy') : ''}
                </div>

                ${booking.grade}${booking.classIdentifier} ${booking.groups?.at(0)?.name}
              </div>
            </div>
          `,
        )}
      </div>
    `;
  };

  const renderMetadata = () => html`
    <div class="flex justify-between">
      <div class="flex flex-wrap items-center gap-2 text-sm">
        ${
          props.mandat.kuerzel
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">Kürzel:</span>
                  <span class="font-medium">${props.mandat.kuerzel}</span>
                </div>
              `
            : nothing
        }
        ${
          props.mandat.rhythm && hasFixedAmount(props.mandat)
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">•</span>
                  <span class="text-base-content/70">Rhythmus:</span>
                  <span class="font-medium">${trans[props.mandat.rhythm]}</span>
                </div>
              `
            : nothing
        }
        ${
          referencedBooking?.grade && referencedBooking.classIdentifier
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">•</span>
                  <span class="text-base-content/70">Klasse:</span>
                  <span class="font-medium">${referencedBooking.grade}${referencedBooking.classIdentifier}</span>
                </div>
              `
            : nothing
        }
        ${
          schoolYear
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">•</span>
                  <span class="text-base-content/70">Schuljahr:</span>
                  <span class="font-medium">${schoolYear}</span>
                </div>
              `
            : nothing
        }
        ${
          props.mandat.oldBooking
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">•</span>
                  <span class="font-medium">Ehemalig</span>
                </div>
              `
            : nothing
        }
      </div>
      ${oldReferencedBookings ? renderOldBookingInfo() : nothing}
    </div>
  `;

  const renderAmount = () => html`
    <div class="flex flex-col gap-2 p-4 bg-base-200/50 rounded-lg ${isDynamic ? 'h-28' : 'h-24'}">
      <span class="text-sm font-semibold text-base-content/70">Betrag ${isDynamic ? '(variabel)' : ''}</span>
      ${
        props.mandat.canEditAmount
          ? html`
              <div class="flex flex-row gap-1 items-center">
                ${NumberInput({
                  ...CentEuroFieldMiddleware(field('amountInCents')),
                  disabled: inputsDisabled,
                  placeholder: 'Betrag in Euro',
                  suffix: '€',
                })}
              </div>
            `
          : html`
              <div class="text-2xl font-semibold text-base-content">
                ${formatMoney(props.mandat.amountInCents ?? undefined) || '-'}
              </div>
            `
      }
      ${
        isDynamic
          ? html`
              <span class="text-sm text-base-content/70">
                Dieser Betrag ist variabel und wird je nach Nutzung verändert.
              </span>
            `
          : nothing
      }
    </div>
  `;

  const renderCustomRhythm = () => html`
    <div class="flex flex-col gap-2 p-4 bg-base-200/50 rounded-lg h-24">
      <span class="text-sm font-semibold text-base-content/70">Rhythmus</span>
      <div class="flex flex-row gap-1 items-center">
        ${DropdownSelectionInput({
          ...field('rhythm'),
          disabled: inputsDisabled,
          selectables: [BillingRhythm.MONTHLY, BillingRhythm.YEARLY],
          placeholder: 'Kein Rhythmus angegeben',
          clearable: true,
          placeholderMode: 'hide',
          renderString: r => trans[r],
        })}
      </div>
    </div>
  `;

  if (referencedBookingLoading || referencedUserLoading || oldReferencedBookingLoading || userLoading)
    return SepaCardSkeleton();

  // if we are at a student show only the student relevants ones. As the endpoint gives back everything connected to the SbS
  if (userIsStudent && referencedUser?._id !== person?._id) return nothing;

  const IntervalInput = html`
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-base-content/70">Start</span>
        ${DateInput({
          ...field('start'),
          // left empty, an unaccepted mandate starts on the day it is accepted (onAccept)
          disabled: inputsDisabled || !startMovable,
          // if the date was migrated to the start of time also show null. the startOfTime is only used for exports
          value: field('start').value
            ? field('start').value?.getTime() === new Date(0).getTime()
              ? null
              : field('start').value
            : null,
          type: 'date',
          placeholder: document.accepted ? 'von' : 'Tag der Zustimmung',
          placeholderMode: 'prefix',
          clearable: !document.accepted,
          // only the administrative roles may backdate a start - see mandateStartNotBackdated
          min: canManageOthers ? undefined : formatCalendarDay(earliestStart, 'yyyy-MM-dd'),
        })}
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-base-content/70">Ende (leer für offenes Ende)</span>
        ${DateInput({
          ...field('end'),
          type: 'date',
          placeholder: 'bis',
          disabled: inputsDisabled,
          placeholderMode: 'prefix',
          clearable: true,
          // only the administrative roles may backdate an end - see mandateEndNotBackdated
          min: canManageOthers ? undefined : formatCalendarDay(immediateEndDay(), 'yyyy-MM-dd'),
        })}
      </div>
    </div>
  `;

  return html`
    <div class="card w-full max-w-[600px] border-4 ${lifecycle.borderClass}">
      <div class="card-body flex flex-col justify-between">
        <div class="flex flex-col gap-4">

        <!-- Header with title and status -->
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <h2 class="font-semibold md:text-lg">${props.mandat.name ?? props.mandat.kuerzel ?? 'SEPA Mandat'}</h2>

              ${canViewGroups && bookingGroup ? html`<a href="/groups/${bookingGroup._id}" class="link link-primary">${bookingGroup.name}</a>` : (bookingGroup?.name ?? '')}
             
            </div>
            <div class="flex items-center gap-1">${statusBadge()} ${optionsMenu()}</div>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="text-base-content/70">Für:</span>
            <span class="font-medium">
              ${referencedUser?.profilDaten?.stammdaten?.vorname}
              ${referencedUser?.profilDaten?.stammdaten?.nachname} (${referencedUser?.debitorennummer})</span
            >
          </div>
        </div>

        <!-- Metadata badges -->
        ${renderMetadata()}

        <!-- Amount - prominent display -->
        ${showSensitiveInputs(document.amountInCents) ? renderAmount() : nothing} ${!hasFixedAmount(props.mandat) && showSensitiveInputs(document.rhythm) ? renderCustomRhythm() : nothing}

        <!-- IBAN - full width -->
         ${
           showSensitiveInputs(document.iban)
             ? html`
                 <div class="flex flex-col gap-2">
                   <span class="text-sm font-semibold text-base-content/70">IBAN</span>
                   ${IBANInput({
                     ...field('iban'),
                     disabled: inputsDisabled,
                     clearable: true,
                     placeholder: 'DE...',
                     placeholderMode: 'hide',
                   })}
                 </div>
               `
             : nothing
         }
        

        ${
          lifecycle.phase === MandatePhase.TEMPLATE && props.mandat.mandateReference
            ? html`<div role="alert" class="alert alert-soft alert-info text-sm">
                Es gibt bereits das beendete Mandat ${props.mandat.mandateReference}. Durch Ihre Zustimmung können Sie
                es reaktivieren.
              </div>`
            : nothing
        }
        ${
          props.mandat.billingLocked && (props.mandat.accepted || document.rhythm !== BillingRhythm.ONCE_CUSTOM)
            ? html`<div role="alert" class="alert alert-soft alert-warning text-sm">
                ${
                  !props.mandat.accepted
                    ? canManageOthers
                      ? 'Die zugehörige Gruppe befindet sich in Abrechnung. In dieser Zeit kann nur die Verwaltung dem Mandat zustimmen.'
                      : 'Die zugehörige Gruppe befindet sich in Abrechnung. Sie können dem Mandat erst nach der Freigabe zustimmen.'
                    : canManageOthers
                      ? 'Die zugehörige Gruppe befindet sich in Abrechnung. In dieser Zeit kann nur die Verwaltung das Mandat ändern.'
                      : 'Die zugehörige Gruppe befindet sich in Abrechnung. In dieser Zeit können Sie keine Änderungen an dem Mandat vornehmen.'
                }
              </div>`
            : nothing
        }

        <!-- Date range - 2 columns -->
        ${
          document.rhythm === BillingRhythm.ONCE_CUSTOM
            ? html`
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-base-content/70">Informationen zum Ablauf des Mandats:</span>
                  <span>
                    ${
                      lifecycle.acceptanceLocked
                        ? 'Die zugehörige Position befindet sich in Abrechnung. In dieser Zeit können Sie keine Änderungen an dem Mandat vornehmen. Sollte es angenommen sein, wird der Betrag automatisch von dem hier hinterlegten Konto eingezogen.'
                        : lifecycle.phase === MandatePhase.BILLED && document.billedOn
                          ? `${canSeeBilledOn ? `Abgerechnet ${formatCalendarDay(document.billedOn, 'MM/yyyy')}. ` : ''}Dieses Mandat wurde einmal ausgeführt und ist nun beendet.`
                          : props.mandat.end && props.mandat.accepted
                            ? `Beendet zum ${formatCalendarDay(props.mandat.end, 'dd.MM.yyyy')}. Dieses Mandat wird nicht mehr eingezogen.`
                            : 'Dieses Mandat wird nur einmal eingezogen. Ab dem Stichtag wird dieses Mandat dann automatisch beendet'
                    }
                  </span>
                </div>
                ${canManageOthers ? IntervalInput : nothing}
              `
            : IntervalInput
        }
        <div class="flex flex-col gap-2">
        ${
          document.accepted && document.acceptedPersonName && document.acceptedAt
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">Zugestimmt durch:</span>
                  <span class="font-medium"
                    >${document.acceptedPersonName} am ${format(document.acceptedAt, 'dd.MM.yyyy')}</span
                  >
                </div>
              `
            : nothing
        }
        ${
          canSeeBilledOn && document.billedOn
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">Letzte Abrechnung:</span>
                  <span class="font-medium">${formatCalendarDay(document.billedOn, 'dd.MM.yyyy')}</span>
                </div>
              `
            : nothing
        }
        ${
          document._id
            ? html`
                <div class="flex items-center gap-1.5">
                  <span class="text-base-content/70">Mandatsreferenz:</span>
                  <span class="font-medium">${document.mandateReference ?? document._id}</span>
                </div>
              `
            : nothing
        }
        </div>
          </div>

        <div class="pt-2 flex flex-row justify-end gap-2 flex-wrap max-w-full">${actionButtons()}</div>
        </div>
      </div>
    </div>
  `;
});
